import { ArchiveX, FileSearch, RefreshCw, TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useClaimPnrDisplay, useRefreshClaimPnrDisplay } from "../api/use-claim-pnr-display";
import { usePnrReference } from "../api/use-pnr-reference";
import {
  PnrDisplayPassengers,
  PnrDisplayRaw,
  PnrDisplayRerouting,
  PnrDisplayTicket,
  PnrDisplayTimeline,
} from "../components/pnr-display/pnr-display-sections";
import { PnrItinerary } from "../components/pnr-display/pnr-itinerary";

/**
 * Amadeus PNR image of a claim (TECH-928).
 *
 * The archive only starts with TECH-961 and Amadeus purges PNRs a few months after travel, so an
 * empty tab is an ordinary outcome — the two reasons are spelled out rather than collapsed into a
 * single "nothing to show".
 */
export function PnrDisplayTab({ claimId }: { claimId: string }) {
  const { data, isPending, isError, refetch, isFetching } = useClaimPnrDisplay(claimId);
  const reference = usePnrReference(claimId);
  const [view, setView] = useState<"readable" | "raw">("readable");

  if (isPending) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={TriangleAlert}
        title="Impossible de charger l'image PNR"
        detail="Le serveur n'a pas répondu. Réessayez dans un instant."
        action={
          <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>
            <RefreshCw className={isFetching ? "animate-spin" : undefined} />
            Réessayer
          </Button>
        }
      />
    );
  }

  if (data.status === "PURGED") {
    return (
      <EmptyState
        icon={ArchiveX}
        title="PNR purgé par Amadeus"
        detail={`Le PNR ${data.gdsPnrId ?? ""} n'était plus disponible lorsque nous l'avons demandé — Amadeus les purge quelques mois après le voyage.${data.sentinel ? ` (${data.sentinel})` : ""}`}
        action={<RefreshFromAmadeus claimId={claimId} />}
      />
    );
  }

  if (data.status === "NEVER_CAPTURED" || !data.view) {
    return (
      <EmptyState
        icon={FileSearch}
        title="Aucune image PNR archivée"
        detail="Ce dossier est antérieur à l'archivage du PNR_Display, ou sa réservation n'est pas un PNR Amadeus."
        action={<RefreshFromAmadeus claimId={claimId} />}
      />
    );
  }

  const pnr = data.view;

  return (
    <div className="flex flex-col gap-4 p-4 text-left">
      <p className="text-sm text-gray-600">
        Dernière image – Réservation telle qu'enregistrée dans Amadeus
        {data.capturedAt ? ` le ${formatCaptureDate(data.capturedAt)}` : ""}.
      </p>
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-lg font-semibold text-gray-950">{pnr.recordLocator}</p>
        <Tabs value={view} onValueChange={(value) => setView(value as "readable" | "raw")}>
          <TabsList>
            <TabsTrigger value="readable">Lisible</TabsTrigger>
            <TabsTrigger value="raw">Brut</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "readable" ? (
        <div className="flex flex-col gap-4">
          <PnrDisplayRerouting view={pnr} />
          <PnrItinerary view={pnr} reference={reference} />
          <PnrDisplayTimeline view={pnr} />
          <PnrDisplayTicket view={pnr} />
          <PnrDisplayPassengers view={pnr} />
        </div>
      ) : (
        <PnrDisplayRaw view={pnr} />
      )}
    </div>
  );
}

/** The S3 write date is an instant; the reader is in France. */
function formatCaptureDate(iso: string) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Paris",
  });
}

function EmptyState({
  icon: Icon,
  title,
  detail,
  action,
}: {
  icon: typeof FileSearch;
  title: string;
  detail?: string;
  action?: React.ReactNode;
}) {
  return (
    <Empty className="py-12 md:py-16">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon />
        </EmptyMedia>
        <EmptyTitle className="text-lg">{title}</EmptyTitle>
        {detail ? <EmptyDescription>{detail}</EmptyDescription> : null}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

/** The image may exist now (a PNR fixed since, a capture that failed): ask Amadeus once more. */
function RefreshFromAmadeus({ claimId }: { claimId: string }) {
  const refresh = useRefreshClaimPnrDisplay(claimId);

  return (
    <>
      <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
        <RefreshCw className={refresh.isPending ? "animate-spin" : undefined} />
        Récupérer depuis Amadeus
      </Button>
      <p className="text-xs text-gray-500">Une demande par réservation toutes les 24 h.</p>
    </>
  );
}
